import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Download, Loader2, Database, Users, MessageSquare, BookOpen, HelpCircle, Trash2, AlertTriangle } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface ExportConfig {
  name: string;
  table: string;
  icon: React.ReactNode;
  description: string;
  canClear: boolean;
  columns?: string;
}

const exportConfigs: ExportConfig[] = [
  {
    name: "Ord",
    table: "words",
    icon: <BookOpen className="h-5 w-5" />,
    description: "Alla ord i databasen",
    canClear: true,
  },
  {
    name: "Ledtrådar",
    table: "clues",
    icon: <HelpCircle className="h-5 w-5" />,
    description: "Alla ledtrådar/synonymer",
    canClear: true,
  },
  {
    name: "Användare",
    table: "profiles",
    icon: <Users className="h-5 w-5" />,
    description: "Alla användarprofiler",
    canClear: false,
  },
  {
    name: "Forumkategorier",
    table: "forum_categories",
    icon: <MessageSquare className="h-5 w-5" />,
    description: "Forumkategorier",
    canClear: false,
  },
  {
    name: "Forumtrådar",
    table: "forum_topics",
    icon: <MessageSquare className="h-5 w-5" />,
    description: "Alla forumtrådar",
    canClear: true,
  },
  {
    name: "Forumsvar",
    table: "forum_replies",
    icon: <MessageSquare className="h-5 w-5" />,
    description: "Alla forumsvar",
    canClear: true,
  },
  {
    name: "Ordförslag",
    table: "word_suggestions",
    icon: <Database className="h-5 w-5" />,
    description: "Inskickade ordförslag",
    canClear: true,
  },
  {
    name: "Ledtrådsförslag",
    table: "clue_suggestions",
    icon: <Database className="h-5 w-5" />,
    description: "Inskickade ledtrådsförslag",
    canClear: true,
  },
  {
    name: "Spärrade ord",
    table: "blocked_words",
    icon: <Database className="h-5 w-5" />,
    description: "Ord som inte ska importeras",
    canClear: true,
  },
  {
    name: "Aktivitetslogg",
    table: "activity_logs",
    icon: <Database className="h-5 w-5" />,
    description: "Systemaktiviteter",
    canClear: true,
  },
];

export function BackupAdmin() {
  const [exporting, setExporting] = useState<string | null>(null);
  const [clearing, setClearing] = useState<string | null>(null);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loadingCounts, setLoadingCounts] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; config: ExportConfig | null }>({
    open: false,
    config: null,
  });

  const fetchCounts = async () => {
    setLoadingCounts(true);
    const newCounts: Record<string, number> = {};
    
    for (const config of exportConfigs) {
      const { count } = await supabase
        .from(config.table as any)
        .select('*', { count: 'exact', head: true });
      newCounts[config.table] = count || 0;
    }
    
    setCounts(newCounts);
    setLoadingCounts(false);
  };

  useEffect(() => {
    fetchCounts();
  }, []);

  const convertToCSV = (data: any[]): string => {
    if (data.length === 0) return '';
    
    const headers = Object.keys(data[0]);
    const csvRows = [headers.join(';')];
    
    for (const row of data) {
      const values = headers.map(header => {
        const val = row[header];
        if (val === null || val === undefined) return '';
        if (typeof val === 'object') return JSON.stringify(val).replace(/"/g, '""');
        const str = String(val);
        if (str.includes(';') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      });
      csvRows.push(values.join(';'));
    }
    
    return csvRows.join('\n');
  };

  const downloadCSV = (csv: string, filename: string) => {
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportTable = async (config: ExportConfig) => {
    setExporting(config.table);
    
    try {
      let allData: any[] = [];
      let offset = 0;
      const pageSize = 1000;
      
      while (true) {
        const { data, error } = await supabase
          .from(config.table as any)
          .select('*')
          .range(offset, offset + pageSize - 1);
        
        if (error) throw error;
        if (!data || data.length === 0) break;
        
        allData = [...allData, ...data];
        
        if (data.length < pageSize) break;
        offset += pageSize;
      }
      
      if (allData.length === 0) {
        toast.info(`Inga data att exportera för ${config.name}`);
        return;
      }
      
      const csv = convertToCSV(allData);
      const date = new Date().toISOString().split('T')[0];
      downloadCSV(csv, `${config.table}_${date}.csv`);
      
      toast.success(`Exporterade ${allData.length} rader från ${config.name}`);
    } catch (error: any) {
      console.error('Export error:', error);
      toast.error(`Kunde inte exportera ${config.name}: ${error.message}`);
    } finally {
      setExporting(null);
    }
  };

  const clearTable = async (config: ExportConfig) => {
    setClearing(config.table);
    setConfirmDialog({ open: false, config: null });
    
    try {
      // Special handling for tables with foreign key dependencies
      if (config.table === 'words') {
        // Delete clues first (they reference words)
        const { error: cluesError } = await supabase.from('clues').delete().neq('id', 0);
        if (cluesError) throw cluesError;
      }
      
      if (config.table === 'forum_topics') {
        // Delete replies first (they reference topics)
        const { error: repliesError } = await supabase.from('forum_replies').delete().neq('id', 0);
        if (repliesError) throw repliesError;
      }
      
      const { error } = await supabase.from(config.table as any).delete().neq('id', 0);
      if (error) throw error;
      
      toast.success(`Tömde ${config.name}`);
      fetchCounts();
    } catch (error: any) {
      console.error('Clear error:', error);
      toast.error(`Kunde inte tömma ${config.name}: ${error.message}`);
    } finally {
      setClearing(null);
    }
  };

  const exportAll = async () => {
    for (const config of exportConfigs) {
      await exportTable(config);
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Backup & Datahantering
          </CardTitle>
          <CardDescription>
            Exportera data till CSV-filer eller töm tabeller. Filerna sparas med semikolon som avgränsare för kompatibilitet med Excel.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button onClick={exportAll} disabled={exporting !== null || clearing !== null} className="w-full">
            {exporting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            Exportera alla tabeller
          </Button>
          
          <div className="grid gap-3 md:grid-cols-2">
            {exportConfigs.map((config) => (
              <div
                key={config.table}
                className="flex items-center justify-between p-3 border rounded-lg bg-card hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="text-muted-foreground">{config.icon}</div>
                  <div>
                    <div className="font-medium">{config.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {config.description}
                      {!loadingCounts && counts[config.table] !== undefined && (
                        <span className="ml-1">({counts[config.table].toLocaleString()} rader)</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => exportTable(config)}
                    disabled={exporting !== null || clearing !== null}
                    title="Exportera"
                  >
                    {exporting === config.table ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )}
                  </Button>
                  {config.canClear && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setConfirmDialog({ open: true, config })}
                      disabled={exporting !== null || clearing !== null || counts[config.table] === 0}
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      title="Töm tabell"
                    >
                      {clearing === config.table ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={confirmDialog.open} onOpenChange={(open) => setConfirmDialog({ open, config: open ? confirmDialog.config : null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Bekräfta tömning
            </AlertDialogTitle>
            <AlertDialogDescription>
              Är du säker på att du vill tömma <strong>{confirmDialog.config?.name}</strong>? 
              Detta tar bort alla {counts[confirmDialog.config?.table || '']?.toLocaleString() || 0} rader permanent.
              {confirmDialog.config?.table === 'words' && (
                <span className="block mt-2 text-destructive font-medium">
                  OBS: Detta tar även bort alla ledtrådar som är kopplade till orden!
                </span>
              )}
              {confirmDialog.config?.table === 'forum_topics' && (
                <span className="block mt-2 text-destructive font-medium">
                  OBS: Detta tar även bort alla forumsvar!
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmDialog.config && clearTable(confirmDialog.config)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Ja, töm tabellen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
